/* eslint complexity: ["error", 34] */
import PropTypes from 'prop-types';
import {
    Button,
    Paragraph,
    Heading,
    Modal,
    Link,
    List,
    ListItem,
    Text as PText,
    Flex,
    useBrand
} from '@cof/plastic-components';
import { useEffect, useState } from 'react';
import { styled, useTheme } from 'styled-components';

import useTechnicalError from '../../utilities/useTechnicalError/useTechnicalError';
import { useAggregatorConsumerName } from '../../utilities/aggregatorConsumerNameProvider';
import { isAggregatorConsumer, isApiPartner } from '../../utilities/utils';
import useIsPtd from '../../utilities/useIsPtd';
import Header from '../../components/Header';
import { withText } from '../../text/textStore';
import Main from '../../components/Main';
import useBackground from '../../utilities/useBackground';
import LoadingSpinner from '../../components/LoadingSpinner';
import getResultPage from '../../components/Quotation/Result/getResult';

import { useObStartData } from '../../data/data';
import Footer from '../../components/Footer/index';
import { addNewRelicPageAction } from '../../utilities/newRelic';
import withExperiment from '../../components/experiments/withExperiment';
import ObLandingHeading from '../../components/OpenBankingLanding/ObLandingHeading';
import { verifyUrlBuilder } from '../../utilities/verifyUrlBuilder';
import { isSafeConnectionUrl } from '../../utilities/isSafeConnectionUrl';
import ObLandingAccordion from '../../components/OpenBankingLanding/ObLandingAccordion';
import ObLandingIconBox, { IconRow } from '../../components/OpenBankingLanding/ObLandingIconBox';
import ObLandingPoweredBy from '../../components/OpenBankingLanding/ObLandingPoweredBy';
import partners from '../../partners';

const StyledMain = styled(Main)`
    padding-right: 1.6rem;
    padding-left: 1.6rem;
    height: 100%;
    align-items: left;
`;

const ExperimentObLandingHeading = withExperiment(ObLandingHeading, 'quotations-ob-landing-heading-mbox');

const ELIGIBILITY_CHECK_UI_CONSUMER_NAMES = ['MSM', 'BRQ_RAID'];

const getMSMSubHeadingText = (Text: any) => () => (
    <Flex flexDirection={'column'} maxWidth={['90%', null, '95%']} alignSelf={['left', 'left', 'left']}>
        <PText
            // @ts-expect-error TS2322: Type '"500"' is not assignable to type 'ResponsiveValue<"regular" | "medium" | "semiBold", DefaultTh
            fontWeight="500"
            size="large"
            color="global.white"
            data-qm-allow
            textAlign={['left', 'left', 'left']}
            paddingBottom={['1.6rem', null, '2.4rem']}
        >
            <Text path="MSMSubtitle1" params={{ 'data-qm-allow': 'true' }} />
            {/* @ts-expect-error TS2322: Type '"700"' is not assignable to type 'ResponsiveValue<"regular" | "medium" | "semiBold", DefaultTh */}
            <PText fontWeight="700" size="large" color="capitalOne.teal450" data-qm-allow>
                {' '}
                <Text path="MSMSubtitle2" params={{ 'data-qm-allow': 'true' }} />
            </PText>
        </PText>
    </Flex>
);

const getOceanSubHeadingText = (Text: any) => () => (
    <Flex flexDirection={'column'} maxWidth={['90%', null, '95%']} alignSelf={['left', 'left', 'left']}>
        <PText
            size="large"
            data-qm-allow
            textAlign={['left', 'left', 'left']}
            paddingBottom={['1.6rem', null, '2.4rem']}
        >
            <Text path="OceanSubtitle1" params={{ 'data-qm-allow': 'true' }} />
            {/* @ts-expect-error TS2322: Type '"600"' is not assignable to type 'ResponsiveValue<"regular" | "medium" | "semiBold", DefaultTh */}
            <PText fontWeight="600" size="large" data-qm-allow>
                {' '}
                <Text path="OceanSubtitle2" params={{ 'data-qm-allow': 'true' }} />
            </PText>
        </PText>
    </Flex>
);

const getDefaultSubHeadingText =
    (subheadingFontWeight: any, headingColor: any, highlightColor: any, isPtd: any) => () => (
        <Flex flexDirection={'column'} maxWidth={['90%', null, '95%']} alignSelf={['left', 'left', 'left']}>
            <PText
                fontWeight={subheadingFontWeight}
                size="large"
                color={headingColor}
                data-qm-allow
                textAlign={['left', 'left', 'left']}
                paddingBottom={['1.6rem', null, '2.4rem']}
            >
                {isPtd
                    ? 'We might be able to offer you a card if you share your banking history.'
                    : 'Sharing your banking history can help us make a decision.'}
                {/* @ts-expect-error TS2322: Type '"700"' is not assignable to type 'ResponsiveValue<"regular" | "medium" | "semiBold", DefaultTh */}
                <PText fontWeight="700" size="large" color={highlightColor} data-qm-allow>
                    {' '}
                    It&apos;s only a few simple steps.
                </PText>
            </PText>
        </Flex>
    );

const getSubtitleParagraph = (isMSMConsumer: any, isPartnerBrand: any, Text: any, paragraphVariant: any) => {
    if (isMSMConsumer) return null;
    const path = isPartnerBrand ? 'OceanSubtitle3' : 'Subtitle';
    return (
        <Paragraph
            data-qa-id="page-paragraph"
            whiteSpace="pre-wrap"
            variant={paragraphVariant}
            // @ts-expect-error TS2322: Type '"medium"' is not assignable to type 'ResponsiveValue<keyof FontSizeKeys, DefaultTheme> | undef
            fontSize="medium"
            paddingBottom={['1.6rem', null, '2.4rem']}
            data-qm-allow
        >
            <Text path={path} params={{ 'data-qm-allow': 'true' }} />
        </Paragraph>
    );
};

const getSubHeadingText = (
    isMSMConsumer: any,
    isPartnerBrand: any,
    Text: any,
    subheadingFontWeight: any,
    headingColor: any,
    highlightColor: any,
    isPtd: any
) => {
    if (isMSMConsumer) return getMSMSubHeadingText(Text);
    if (isPartnerBrand) return getOceanSubHeadingText(Text);
    return getDefaultSubHeadingText(subheadingFontWeight, headingColor, highlightColor, isPtd);
};

const getTitle = (isEligibilityCheckUI: any, isMSMConsumer: any) => {
    if (isMSMConsumer) return 'MSMTitle';
    if (isEligibilityCheckUI) return 'EligibilityCheckTitle';
    return 'Title';
};

const getAccordion1 = (isEligibilityCheckUI: any, isPartnerBrand: any) => {
    if (isPartnerBrand) return 'OceanAccordion1';
    if (isEligibilityCheckUI) return 'EligibilityCheckAccordion1';
    return 'Accordion1';
};

const getEligibilityCheckConfiguration = (
    // @ts-expect-error TS7006: Parameter 'isEligibilityCheckUI' implicitly has an 'any' type.
    isEligibilityCheckUI,
    // @ts-expect-error TS7006: Parameter 'isMSMConsumer' implicitly has an 'any' type.
    isMSMConsumer,
    // @ts-expect-error TS7006: Parameter 'isOceanBrand' implicitly has an 'any' type.
    isPartnerBrand,
    // @ts-expect-error TS7006: Parameter 'onNoThanksClick' implicitly has an 'any' type.
    onNoThanksClick,
    // @ts-expect-error TS7006: Parameter 'Text' implicitly has an 'any' type.
    Text,
    // @ts-expect-error TS7006: Parameter 'paragraphVariant' implicitly has an 'any' type.
    paragraphVariant,
    // @ts-expect-error TS7006: Parameter 'subheadingFontWeight' implicitly has an 'any' type.
    subheadingFontWeight,
    // @ts-expect-error TS7006: Parameter 'headingColor' implicitly has an 'any' type.
    headingColor,
    // @ts-expect-error TS7006: Parameter 'highlightColor' implicitly has an 'any' type.
    highlightColor,
    // @ts-expect-error TS7006: Parameter 'isPtd' implicitly has an 'any' type.
    isPtd
) => ({
    title: getTitle(isEligibilityCheckUI, isMSMConsumer),
    titlePaddingTop: isEligibilityCheckUI ? ['2.4rem', null, '3.2rem'] : null,
    titleAlignment: 'left',

    SubHeadingText: getSubHeadingText(
        isMSMConsumer,
        isPartnerBrand,
        Text,
        subheadingFontWeight,
        headingColor,
        highlightColor,
        isPtd
    ),
    SubtitleParagraph: getSubtitleParagraph(isMSMConsumer, isPartnerBrand, Text, paragraphVariant),
    accordion1: getAccordion1(isEligibilityCheckUI, isPartnerBrand),
    noThanksButton: isEligibilityCheckUI ? null : (
        <IconRow>
            <Button
                data-qa-id="OB-no-thanks-button"
                variant="textLink"
                text
                onClick={onNoThanksClick}
                // @ts-expect-error TS2322: Type '"0"' is not assignable to type 'ResponsiveValue<AllowedSpace, DefaultTheme> | undefined'.
                px={'0'}
                textAlign="center"
                data-qm-allow
            >
                No thanks, cancel my eligibility check
            </Button>
        </IconRow>
    ),

    buttonFlexPaddingBottom: isEligibilityCheckUI ? ['0.8rem', null, '0.8rem'] : null
});

// Partners vary the casing of the pcr query param (e.g. Ocean sends pCR, MSM sends PCR)
const getPcr = (params: any, consumerName: any, brand: any) => {
    if (!isAggregatorConsumer(consumerName) && !isApiPartner(brand)) return undefined;
    const pcrKey = [...params.keys()].find((key) => key.toLowerCase() === 'pcr');
    return pcrKey ? params.get(pcrKey) : null;
};

const OpenBankingLanding = ({ Text, Text: text }: any) => {
    const [isAreYouSureModalOpen, setAreYouSureModalOpen] = useState(false);
    const [isAboutDOneModalOpen, setIsAboutDOneModalOpen] = useState(false);

    const theme = useTheme();
    // @ts-expect-error TS2339: Property 'pages' does not exist on type 'DefaultTheme'.
    const pageTheme = theme?.pages?.['openBanking'];
    const params = new URLSearchParams(window.location.search);
    const brand = useBrand();
    const quotation = params.get('quotation');
    const consumerName = params.get('consumerName');
    const productCode = params.get('p');
    const pcr = getPcr(params, consumerName, brand);
    const s = isApiPartner(brand) ? params.get('s') : undefined;

    const [data, loading, { response, error }] = useObStartData({
        quotation,
        ...(productCode && { productCode }),
        ...(pcr && { pcr }),
        ...(s && { s })
    });

    const [, setAggregatorConsumerName] = useAggregatorConsumerName();

    useTechnicalError(error);

    // Log consumerName page visit to New Relic when the page loads
    useEffect(() => {
        if (consumerName) {
            addNewRelicPageAction('OB Landing Page Visit - Consumer Name', {
                consumerName: consumerName,
                hasQuotation: !!quotation
            });
        }
    }, [consumerName, quotation]);

    // Log when Consumer Name traffic successfully loads OB landing with connection URL
    useEffect(() => {
        if (consumerName && data !== 'Session expired' && data?.connectionUrl && !loading) {
            addNewRelicPageAction('OB Landing - Consumer Name Code Success', {
                consumerName: consumerName,
                hasConnectionUrl: true
            });
        }
    }, [consumerName, data, loading]);

    const [declineOb, setDeclineOb] = useState(false);
    const [isObDeclineConsent, setIsObDeclineConsent] = useState(false);
    const background = useBackground('openBanking');

    // @ts-expect-error TS2345: Argument of type 'string | null' is not assignable to parameter of type 'string'.
    const isEligibilityCheckUI = ELIGIBILITY_CHECK_UI_CONSUMER_NAMES.includes(consumerName);
    const isMSMConsumer = consumerName === 'MSM';
    const isPartnerBrand = brand != partners.DEFAULT;
    const isPtd = useIsPtd();
    const dynamicComponents = getEligibilityCheckConfiguration(
        isEligibilityCheckUI,
        isMSMConsumer,
        isPartnerBrand,
        () => setAreYouSureModalOpen(true),
        Text,
        pageTheme?.paragraphVariant,
        pageTheme?.subheadingFontWeight,
        pageTheme?.headingColor,
        pageTheme?.highlightColor,
        isPtd
    );

    useEffect(() => {
        if (consumerName && isAggregatorConsumer(consumerName)) {
            setAggregatorConsumerName(consumerName);
        }
    }, [consumerName, setAggregatorConsumerName]);

    useEffect(() => {
        if (response?.status == 200 && data !== 'Session expired' && !data?.connectionUrl && data?.details) {
            setDeclineOb(true);
            // Log when consumer name traffic leads to decline/error page
            if (consumerName) {
                addNewRelicPageAction('OB Landing - Consumer Name Declined', {
                    consumerName: consumerName
                });
            }
        }
    }, [response, consumerName, data]);

    if (data === 'Session expired') {
        const redirectToVerifyUrl = verifyUrlBuilder({ brand, quotation, consumerName, productCode, pcr, s });
        addNewRelicPageAction('OB Landing - Redirected to Verify', { consumerName, reason: 'session_expired' });
        window.location.assign(redirectToVerifyUrl.toString());
        return null;
    }

    if (!quotation || (response?.status == 200 && !data?.details)) {
        window.location.assign(window.location.origin);
    }

    return (
        <>
            {declineOb && data?.details ? (
                getResultPage(data?.details, isObDeclineConsent)
            ) : (
                <>
                    <Header isObLanding={true} />
                    <StyledMain bg={background} data-qa-id="ob-landing-page" alignItems={'center'}>
                        {loading || data === undefined ? (
                            <LoadingSpinner features={{ resultSpinner: true }} data-qa-id="OB-landing-spinner" />
                        ) : (
                            <Flex flexDirection={'column'} maxWidth={['100%', null, '650px']}>
                                <ExperimentObLandingHeading
                                    paddingTop={dynamicComponents.titlePaddingTop}
                                    textAlign={dynamicComponents.titleAlignment}
                                    Text={Text}
                                    titlePath={dynamicComponents.title}
                                />
                                {dynamicComponents.SubHeadingText()}
                                {dynamicComponents.SubtitleParagraph}
                                <ObLandingAccordion
                                    paragraphVariant={pageTheme?.paragraphVariant}
                                    textColor={pageTheme?.textColor}
                                    accordion1Path={dynamicComponents.accordion1}
                                    Text={Text}
                                    text={text}
                                    onAboutDOneClick={() => setIsAboutDOneModalOpen(true)}
                                />
                                <ObLandingIconBox
                                    iconBox={pageTheme?.iconBox}
                                    iconColour={pageTheme?.iconColour}
                                    connectionUrl={data?.connectionUrl}
                                    buttonFlexPaddingBottom={dynamicComponents.buttonFlexPaddingBottom}
                                    noThanksButton={dynamicComponents.noThanksButton}
                                    Text={Text}
                                />
                                <ObLandingPoweredBy
                                    paragraphVariant={pageTheme?.paragraphVariant}
                                    dOneLogo={pageTheme?.dOneLogo}
                                />
                                <Modal
                                    // @ts-expect-error TS2322: Type 'Element' is not assignable to type 'string'.
                                    heading={
                                        <Heading level="3" textAlign="left" data-qm-allow>
                                            Are you sure?
                                        </Heading>
                                    }
                                    id="are-you-sure-modal"
                                    rootId={'root'}
                                    aria-modal="true"
                                    onClose={() => setAreYouSureModalOpen(false)}
                                    isOpen={isAreYouSureModalOpen}
                                    modalWrapperProps={{
                                        // @ts-expect-error TS2353: Object literal may only specify known properties, and 'marginLeft' does not exist in type 'WidthProp
                                        marginLeft: '0.8rem',
                                        marginRight: '0.8rem'
                                    }}
                                >
                                    {/* @ts-expect-error TS2322: Type '"400"' is not assignable to type 'ResponsiveValue<"regular" | "medium" | "semiBold", DefaultTh */}
                                    <Paragraph data-qa-id="modal-default-text" fontWeight="400" data-qm-allow>
                                        <Text path="AreYouSureModal" params={{ 'data-qm-allow': 'true' }} />
                                    </Paragraph>
                                    <Flex
                                        flexDirection={'column'}
                                        maxWidth="100%"
                                        alignItems={'center'}
                                        paddingTop={'0.8rem'}
                                    >
                                        <Button
                                            data-qa-id="OB-I-understand-button"
                                            onClick={() => {
                                                setIsObDeclineConsent(true);
                                                setDeclineOb(true);
                                            }}
                                            width={['311px', null, '311px']}
                                            data-qm-allow
                                        >
                                            I understand
                                        </Button>
                                        <Button
                                            data-qa-id="OB-modal-link-account-button"
                                            mt="xs"
                                            variant="secondary"
                                            onClick={() => {
                                                if (isSafeConnectionUrl(data?.connectionUrl)) {
                                                    window.location.assign(data.connectionUrl);
                                                }
                                            }}
                                            width={['311px', null, '311px']}
                                            data-qm-allow
                                        >
                                            Link my bank account
                                        </Button>
                                    </Flex>
                                </Modal>
                                <Modal
                                    // @ts-expect-error TS2322: Type 'Element' is not assignable to type 'string'.
                                    heading={
                                        <Heading level="3" textAlign="left" data-qm-allow>
                                            About D•One
                                        </Heading>
                                    }
                                    rootId={'root'}
                                    aria-modal="true"
                                    data-qa-id="about-done-modal"
                                    onClose={() => setIsAboutDOneModalOpen(false)}
                                    isOpen={isAboutDOneModalOpen}
                                    modalWrapperProps={{
                                        // @ts-expect-error TS2353: Object literal may only specify known properties, and 'marginLeft' does not exist in type 'WidthProp
                                        marginLeft: '0.8rem',
                                        marginRight: '0.8rem'
                                    }}
                                >
                                    <List data-qa-id="about-done-default-list" iconColor={pageTheme?.bulletColor}>
                                        <ListItem data-qa-id="about-done-list-item-1">
                                            {/* @ts-expect-error TS2322: Type '"medium"' is not assignable to type 'ResponsiveValue<keyof FontSizeKeys, DefaultTheme> | undef */}
                                            <Paragraph fontSize="medium" lineHeight="medium">
                                                <PText data-qm-allow>D•One (</PText>
                                                <Link
                                                    external
                                                    href={text({
                                                        path: 'AboutDOneModalTextParagraph1.Link1.Target'
                                                    })}
                                                    variant={'dark'}
                                                    data-qa-id="done-link"
                                                    quantumMetricVisible={true}
                                                >
                                                    <Text
                                                        fontWeight="500"
                                                        path="AboutDOneModalTextParagraph1.Link1.Text"
                                                        params={{ 'data-qm-allow': 'true' }}
                                                    />
                                                </Link>
                                                <PText data-qm-allow>
                                                    ) (“D•One”) is our Open Banking partner. By connecting your account
                                                    with D•One, you will be able to securely share your account
                                                    information with us.
                                                </PText>
                                            </Paragraph>
                                        </ListItem>
                                        <ListItem data-qa-id="about-done-list-item-2">
                                            {/* @ts-expect-error TS2322: Type '"medium"' is not assignable to type 'ResponsiveValue<keyof FontSizeKeys, DefaultTheme> | undef */}
                                            <Paragraph fontSize="medium" lineHeight="medium" data-qm-allow>
                                                <Text path="DOneModal2" params={{ 'data-qm-allow': 'true' }} />
                                            </Paragraph>
                                        </ListItem>
                                        <ListItem data-qa-id="about-done-list-item-3">
                                            {/* @ts-expect-error TS2322: Type '"medium"' is not assignable to type 'ResponsiveValue<keyof FontSizeKeys, DefaultTheme> | undef */}
                                            <Paragraph fontSize="medium" lineHeight="medium">
                                                <PText data-qm-allow>
                                                    By proceeding, you agree to D•One&apos;s Terms of Service (
                                                </PText>
                                                <Link
                                                    external
                                                    href={text({
                                                        path: 'AboutDOneModalTextParagraph3.Link1.Target'
                                                    })}
                                                    variant={'dark'}
                                                    quantumMetricVisible={true}
                                                >
                                                    <Text
                                                        fontWeight="500"
                                                        path="AboutDOneModalTextParagraph3.Link1.Text"
                                                        params={{ 'data-qm-allow': 'true' }}
                                                    />
                                                </Link>
                                                <PText data-qm-allow>
                                                    ). D•One will treat your account information and personal data in
                                                    accordance with their Privacy Policy (
                                                </PText>
                                                <Link
                                                    external
                                                    href={text({
                                                        path: 'AboutDOneModalTextParagraph3.Link2.Target'
                                                    })}
                                                    variant={'dark'}
                                                    quantumMetricVisible={true}
                                                >
                                                    <Text
                                                        fontWeight="500"
                                                        path="AboutDOneModalTextParagraph3.Link2.Text"
                                                        params={{ 'data-qm-allow': 'true' }}
                                                    />
                                                </Link>
                                                <PText data-qm-allow>) .</PText>
                                            </Paragraph>
                                        </ListItem>
                                        <ListItem data-qa-id="about-done-list-item-4">
                                            {/* @ts-expect-error TS2322: Type '"medium"' is not assignable to type 'ResponsiveValue<keyof FontSizeKeys, DefaultTheme> | undef */}
                                            <Paragraph fontSize="medium" lineHeight="medium" data-qm-allow>
                                                <Text path="DOneModal4" params={{ 'data-qm-allow': 'true' }} />
                                            </Paragraph>
                                        </ListItem>
                                    </List>
                                </Modal>
                            </Flex>
                        )}
                    </StyledMain>
                </>
            )}
            <Footer />
        </>
    );
};
OpenBankingLanding.propTypes = {
    Text: PropTypes.func.isRequired,
    TemplateText: PropTypes.func
};

const OpenBankingLandingWithText = withText('OpenBankingLanding')(OpenBankingLanding);
OpenBankingLandingWithText.displayName = 'OpenBankingLanding';

export default OpenBankingLandingWithText;
